/* =========================================
   🌍 FULL Currency Translation (ALL ISO)
   Uses Intl.DisplayNames (CLDR) + fallback
   ========================================= */

const currencyDisplayCache = new Map(); // key: locale -> Intl.DisplayNames

function getCurrencyDisplay(locale) {
  if (currencyDisplayCache.has(locale)) return currencyDisplayCache.get(locale);

  const display =
    typeof Intl !== "undefined" &&
    typeof Intl.DisplayNames !== "undefined"
      ? new Intl.DisplayNames([locale], { type: "currency" })
      : null;

  currencyDisplayCache.set(locale, display);
  return display;
}

// Fallback for key currencies if Intl.DisplayNames is not available
const FALLBACK_AR = {
  SDG: "جنيه سوداني",
  USD: "دولار أمريكي",
  EUR: "يورو",
  GBP: "جنيه إسترليني",
  JPY: "ين ياباني",
  SAR: "ريال سعودي",
  AED: "درهم إماراتي",
  EGP: "جنيه مصري",
  QAR: "ريال قطري",
  KWD: "دينار كويتي",
  BHD: "دينار بحريني",
  OMR: "ريال عُماني",
  JOD: "دينار أردني",
  MAD: "درهم مغربي",
  TND: "دينار تونسي",
  DZD: "دينار جزائري",
  TRY: "ليرة تركية",
  INR: "روبية هندية",
  CNY: "يوان صيني",
  CAD: "دولار كندي",
  AUD: "دولار أسترالي",
  CHF: "فرنك سويسري",
  ZAR: "راند جنوب أفريقي",
  NGN: "نايرا نيجيرية",
  ETB: "بير إثيوبي",
  KES: "شلن كيني",
  UGX: "شلن أوغندي",
};

/**
 * Get localized currency name using Intl.DisplayNames (CLDR) with fallback
 * @param {string} code - ISO currency code (e.g., "USD", "SDG")
 * @param {string} lang - Language code ("en" or "ar")
 * @returns {string} - Localized currency name
 */
export function getCurrencyName(code, lang = "en") {
  const upper = String(code || "").toUpperCase().trim();
  if (!upper) return "";

  const locale = lang === "ar" ? "ar" : "en";
  const display = getCurrencyDisplay(locale);

  // 1) Best: CLDR translation via Intl.DisplayNames
  if (display) {
    try {
      const name = display.of(upper);
      if (name && name !== upper) return name;
    } catch (e) {
      // Invalid currency code - fall through to fallback
    }
  }

  // 2) Fallback: Arabic manual map
  if (lang === "ar" && FALLBACK_AR[upper]) return FALLBACK_AR[upper];

  // 3) Last fallback: just show the code
  return upper;
}

/**
 * Format price with localized currency name
 * Arabic: "17,000 جنيه سوداني"
 * English: "USD 17,000"
 * 
 * @param {number} amount - The price amount
 * @param {string} currencyCode - ISO currency code (e.g., "USD", "SDG")
 * @param {string} lang - Language code ("en" or "ar")
 * @returns {string} - Formatted price string
 */
export function formatPrice(amount, currencyCode, lang = "en") {
  const code = String(currencyCode || "SDG").toUpperCase().trim();

  const localeNumber = lang === "ar" ? "ar-SD" : "en-US";
  const number = new Intl.NumberFormat(localeNumber, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(Number(amount) || 0);

  const currencyName = getCurrencyName(code, lang);

  // Arabic: "17,000 جنيه سوداني"
  if (lang === "ar") return `${number} ${currencyName}`;

  // English: "USD 17,000"
  return `${code} ${number}`;
}

/* ===== Usage Examples =====
formatPrice(17000, "SDG", "ar") -> "١٧٬٠٠٠ جنيه سوداني"
formatPrice(17000, "USD", "ar") -> "١٧٬٠٠٠ دولار أمريكي"
formatPrice(17000, "EUR", "ar") -> "١٧٬٠٠٠ يورو"
formatPrice(17000, "JPY", "ar") -> "١٧٬٠٠٠ ين ياباني"
formatPrice(17000, "SDG", "en") -> "SDG 17,000"
*/
